import { Box, Container, Heading, HeadingProps, Text, TextProps } from '@chakra-ui/react';
import {
  ContentWithTOCWrapper,
  getHeadingId,
  useVisibleSection,
} from '@components/PageWithTableOfContents';
import {
  AppLink,
  GradientShadowOverlay,
  LANDING_ROUTES,
  pxToRem,
  TabsContainer,
  TabsItem,
  TermsSlugType,
} from '@cyberkongz/ui';
import { PageSEOTags } from '@features/seo/SEO';
import { MDXProvider } from '@mdx-js/react';
import { PropsWithChildren, useMemo } from 'react';

import { TERMS_CONTENT } from './content';

const TABS: { id: TermsSlugType; label: string }[] = [
  { id: 'terms-and-conditions', label: 'Terms and conditions' },
  { id: 'privacy-policy', label: 'Privacy policy' },
  { id: 'cookies-policy', label: 'Cookies policy' },
  { id: 'genkai-saga-disclosures', label: 'Genkai / Saga Disclosures' },
  { id: 'kongz-kreator-program', label: 'Kongz Kreator Program' },
  { id: 'ck-store-and-emaki-policy', label: 'CK Store and Emaki Policy' },
  { id: 'ck-ar-experience-policy', label: 'CK AR Experience Policy' },
];

function TermsTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: TermsSlugType;
  setActiveTab: (activeTab: TermsSlugType) => void;
}) {
  return (
    <Container>
      <TabsContainer css={{ mt: 10, flexDirection: ['column', null, 'row'] }}>
        {TABS.map(({ id, label }) => (
          <AppLink href={LANDING_ROUTES.TERMS(id)} variant="noHover" key={id}>
            <TabsItem {...{ activeTab, setActiveTab, tab: id, key: id }}>
              <Heading size="2xs" color="inherit">
                {label}
              </Heading>
            </TabsItem>
          </AppLink>
        ))}
      </TabsContainer>
    </Container>
  );
}

function IntroSection() {
  return (
    <Box
      css={{
        position: 'relative',
        pt: [32, null, pxToRem(196)],
        pb: [16, null, pxToRem(96)],
        bg: 'url(/static/terms/bg.jpg) center / cover no-repeat',
      }}
    >
      <Container position="relative" zIndex={2}>
        <Heading variant="displaySecondary">Terms</Heading>
      </Container>
      <GradientShadowOverlay height={pxToRem(110)} />
    </Box>
  );
}

function TermsContent({
  activeTab,
  setHeadingRef,
}: {
  activeTab: TermsSlugType;
  setHeadingRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <Box
      css={{
        flex: 1,
        w: '100%',
        maxWidth: ['100%', null, pxToRem(720)],
        fontSize: ['sm', 'lg'],
        ul: {
          pl: 10,
          mb: 5,
        },
      }}
    >
      <MDXProvider
        components={{
          h1: (props: HeadingProps) => (
            <Heading
              as="h1"
              // assume headings are just strings
              id={getHeadingId(props.children as string)}
              ref={setHeadingRef}
              size="md"
              css={{ mt: 16, mb: 5, '&:first-of-type': { mt: 0 } }}
              {...props}
            />
          ),
          h2: (props: HeadingProps) => (
            <Heading
              as="h2"
              // assume headings are just strings
              id={getHeadingId(props.children as string)}
              ref={setHeadingRef}
              size="sm"
              css={{ mt: 16, mb: 5 }}
              {...props}
            />
          ),
          p: (props: TextProps) => <Text css={{ mb: 5, '&:last-of-type': { mb: 0 } }} {...props} />,
          // @ts-ignore
          a: (props) => <AppLink css={{ textDecoration: 'underline' }} {...props} />,
        }}
      >
        {/* @ts-ignore */}
        {TERMS_CONTENT[activeTab].content}
      </MDXProvider>
    </Box>
  );
}

export function TermsLayout({ children, terms }: PropsWithChildren & { terms: TermsSlugType }) {
  const headings = useMemo(() => TERMS_CONTENT[terms].headings, [terms]);
  const { visibleSection, headingRefs } = useVisibleSection(headings);

  return (
    <Box css={{ width: '100%', color: 'textSecondary' }}>
      <PageSEOTags pageKey={`terms.${terms}`} />
      {children}
      <IntroSection />
      <TermsTabs
        {...{
          activeTab: terms,
          setActiveTab: () => undefined,
        }}
      />
      <ContentWithTOCWrapper visibleSection={visibleSection} headings={headings}>
        <TermsContent
          {...{
            activeTab: terms,
            setHeadingRef: (el) => {
              if (el) {
                const index = headings.findIndex(
                  (heading) => heading === el.innerText.replace(/[.:]/, '')
                );

                headingRefs.current[index] = el;
              }
            },
          }}
        />
      </ContentWithTOCWrapper>
    </Box>
  );
}
